import React, { useState, useEffect, useMemo } from 'react'
import api from '@utils/api'
import { Texto, GraviGrid } from '@gravitate-js/excalibrr'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useUserSettings } from '@hooks/settings'
import { useLoadNumbers } from '@api/useLoadNumbers'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'load-number'

export const LoadNumbers = () => {
  const { useLoadNumbersQuery } = useLoadNumbers()
  const { data: rowData, refetch: getLoadNumbers } = useLoadNumbersQuery()
  const { updateSetting } = useUserSettings()
  const agPropOverrides = useMemo(
    () => ({
      getRowId: (row) => row?.data?.key,
      frameworkComponents: {
        loadNumberCellRenderer: LoadNumberCellRenderer,
        counterpartyCellRenderer: CounterpartyCellRenderer,
      },
    }),
    []
  )
  const columnDefs = useMemo(getColumnDefs, [])
  const { scopes } = useSecurityContext()

  return (
    <GraviGrid
      storageKey={STORAGE_KEY}
      controlBarProps={{
        title: 'Load Number Management',
        actionButtons: (
          <UploadDownloadButtons
            refreshCallback={getLoadNumbers}
            excelOptions={[
              {
                title: 'Download Load Numbers',
                downloadEP: api.downloadLNExcel,
                fileName: 'Load Numbers.xlsx',
              },
              {
                title: 'Upload Load Numbers',
                uploadEP: scopes?.price_management?.edit && api.uploadLoadNumbers,
              },
              {
                title: 'Download Blend Codes',
                downloadEP: api.downloadBlendCodes,
                fileName: 'Blend Codes.xlsx',
              },
              {
                title: 'Upload Blend Codes',
                uploadEP: scopes?.price_management?.edit && api.uploadBlendCodes,
              },
              {
                title: 'Download Destination Codes',
                downloadEP: api.downloadDestinationCodes,
                fileName: 'Destination Codes.xlsx',
              },
              {
                title: 'Upload Destination Codes',
                uploadEP: scopes?.price_management?.edit && api.uploadDestinationCodes,
              },
              {
                title: 'Download Product Codes',
                downloadEP: api.downloadProductCodes,
                fileName: 'Product Codes.xlsx',
              },
              {
                title: 'Upload Product Codes',
                uploadEP: scopes?.price_management?.edit && api.uploadProductCodes,
              },
            ]}
          />
        ),
      }}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      rowData={rowData?.rows || []}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
const LoadNumberCellRenderer = (loadNumber) => {
  return <Texto weight='bold'>{loadNumber.value}</Texto>
}

const CounterpartyCellRenderer = (counterparty) => {
  return <Texto>{counterparty.value ? counterparty.value : 'N/A'}</Texto>
}

const getColumnDefs = () => [
  {
    field: 'supply_owner',
    enableRowGroup: true,
    headerName: 'Supply Owner',
    cellRenderer: 'counterpartyCellRenderer',
  },
  {
    field: 'supplier',
    enableRowGroup: true,
  },
  {
    field: 'terminal',
    enableRowGroup: true,
  },
  {
    field: 'supply_type',
    headerName: 'Supply Type',
    enableRowGroup: true,
  },
  {
    field: 'carrier',
    enableRowGroup: true,
  },
  {
    field: 'product',
    enableRowGroup: true,
  },
  {
    field: 'contract',
    enableRowGroup: true,
  },
  {
    field: 'destination_site',
    headerName: 'Destination Site',
    enableRowGroup: true,
  },
  {
    field: 'destination_state',
    enableRowGroup: true,
    headerName: 'Destination State',
  },
  {
    field: 'notes',
  },
  {
    field: 'load_number',
    headerName: 'Load Number',
    cellRenderer: 'loadNumberCellRenderer',
    cellRendererParams: { weight: 'bold' },
    comparator: (a, b) => {
      return a?.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    },
  },
]
